import React, {useEffect, useRef, useState} from "react";
import logo_sm from './logo-sm.png'
import localforage from "localforage";
import { Link } from "react-router-dom";
import { buildApiUrl, getStaffDetails, logout, useBaseUrl } from "../../utils/utils";
import { showToast } from "../../utils/swal";
import InstitutionAIAssistant from "./InstitutionAIAssistant";

type InboxTab = 'all' | 'messages' | 'alerts' | 'notifications';

interface CommunicationInboxItem {
    inbox_id: string;
    item_type: 'notification' | 'message' | 'alert';
    priority: 'low' | 'medium' | 'high' | 'critical';
    title: string;
    body: string;
    action_url?: string;
    action_label?: string;
    created_at: string;
    is_read: boolean;
    sender_name?: string;
}

interface InboxCounts {
    all: number;
    messages: number;
    alerts: number;
    notifications: number;
    unread_total: number;
    unread_messages: number;
    unread_alerts: number;
    unread_notifications: number;
}

const emptyInboxCounts = (): InboxCounts => ({
    all: 0,
    messages: 0,
    alerts: 0,
    notifications: 0,
    unread_total: 0,
    unread_messages: 0,
    unread_alerts: 0,
    unread_notifications: 0,
});

const DashboardHeader: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [fname, setFname] = useState<any>("")
    const [lname, setLname] = useState<any>("")
    const [clinicName, setClinicName] = useState<any>("")
    const [staffId, setStaffId] = useState<any>("")
    const [institutionId, setInstitutionId] = useState<any>("")
    const [avatar, setAvatar] = useState<any>("avatar.png")
    const [wallet, setWallet] = useState<any>(0)
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [activeInboxTab, setActiveInboxTab] = useState<InboxTab>('all');
    const [inboxCounts, setInboxCounts] = useState<InboxCounts>(emptyInboxCounts());
    const [inboxItems, setInboxItems] = useState<Record<InboxTab, CommunicationInboxItem[]>>({ all: [], messages: [], alerts: [], notifications: [] });
    const [inboxLoading, setInboxLoading] = useState(false);
    const [inboxError, setInboxError] = useState<string>('');
    const seenInboxItemIdsRef = useRef<Set<string>>(new Set());
    const hasInitializedInboxRef = useRef(false);

    const toggleFullscreen = () => {
        if (!isFullscreen) {
            document.documentElement.requestFullscreen();
        } else {

            document.exitFullscreen();
        }

        setIsFullscreen(!isFullscreen);

    }

    const [currentMode, setCurrentMode] = useState<string>("system")

    const SwitchMode = (currentMode: string) => {

        if(currentMode === 'light'){

            document.documentElement.setAttribute('data-layout-mode', 'dark')

        }else if(currentMode==="dark"){

            document.documentElement.setAttribute('data-layout-mode', 'light')

        }

    }

    const handleLogout = async () => {
        await logout();
    }

    useEffect(()=>{
        // Use the new utility function to get staff details
        const loadStaffDetails = async () => {
            try {
                const staffDetails = await getStaffDetails();
                if (staffDetails) {
                    setFname(staffDetails.fname || "");
                    setLname(staffDetails.lname || "");
                    setClinicName(staffDetails.institution || "");
                    setStaffId(staffDetails.staff_id || "");
                    setInstitutionId(staffDetails.institution_id || "");
                } else {
                    // Fallback to individual localForage items
                    localforage.getItem("fname").then((first_name)=>{
                        setFname(first_name || "")
                    })

                    localforage.getItem("lname").then((last_name)=>{
                        setLname(last_name || "")
                    })

                    localforage.getItem("clinic_name").then((clinic)=>{
                        setClinicName(clinic || "")
                    })

                    localforage.getItem("staff_id").then((usr)=>{
                        setStaffId(usr || "")
                    })

                    localforage.getItem("institution_id").then((inst)=>{
                        setInstitutionId(inst || "")
                    })
                }

                localforage.getItem("user_avatar").then((avtr)=>{
                    if(avtr === "" || avtr === null){
                        setAvatar("avatar.png")
                    }else{
                        setAvatar(avtr)
                    }
                })
            } catch (error) {
                console.error('Error loading staff details:', error);
            }
        };

        loadStaffDetails();
    }, [])

    useEffect(() => {
        if (!staffId || !institutionId) {
            return;
        }

        const RefreshInboxListener = () => {
            RefreshInboxData(false);
        };

        RefreshInboxData(true);
        const refreshTimer = window.setInterval(() => {
            RefreshInboxData(false);
        }, 12000);

        window.addEventListener('uzima:inbox-updated', RefreshInboxListener);

        return () => {
            window.clearInterval(refreshTimer);
            window.removeEventListener('uzima:inbox-updated', RefreshInboxListener);
        };
    }, [staffId, institutionId, baseUrl]);

    const BuildInboxQuery = (tab?: InboxTab) => {
        const params = new URLSearchParams({
            staff_id: String(staffId),
            institution_id: String(institutionId),
            page: '1',
            page_size: '5'
        });

        if (tab) {
            params.set('tab', tab);
        }

        return params.toString();
    }

    const FetchInboxSummary = async () => {
        const response = await fetch(buildApiUrl(baseUrl, `communications/api/inbox/summary/?${BuildInboxQuery()}`), {
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        });

        if (!response.ok) {
            throw new Error('Failed to load inbox summary');
        }

        const payload = await response.json();
        const counts = payload?.data?.counts || emptyInboxCounts();
        setInboxCounts(counts);
        return counts;
    }

    const FetchInboxTabItems = async (tab: InboxTab) => {
        const response = await fetch(buildApiUrl(baseUrl, `communications/api/inbox/items/?${BuildInboxQuery(tab)}`), {
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to load ${tab} inbox items`);
        }

        const payload = await response.json();
        const items = Array.isArray(payload?.data?.items) ? payload.data.items : [];
        setInboxItems((prev) => ({ ...prev, [tab]: items }));
        return items;
    }

    const ShowIncomingInboxToasts = (items: CommunicationInboxItem[]) => {
        const newUnreadItems = items.filter((item) => !item.is_read && !seenInboxItemIdsRef.current.has(item.inbox_id));

        items.forEach((item) => {
            seenInboxItemIdsRef.current.add(item.inbox_id);
        });

        if (!hasInitializedInboxRef.current) {
            hasInitializedInboxRef.current = true;
            return;
        }

        newUnreadItems.slice(0, 3).reverse().forEach((item) => {
            showToast({
                icon: item.item_type === 'alert' ? 'warning' : item.item_type === 'message' ? 'info' : 'success',
                title: item.title,
                text: item.body,
                timer: 6500,
            });
        });
    };

    const RefreshInboxData = async (showLoader: boolean = true) => {
        if (!staffId || !institutionId) {
            return;
        }

        try {
            if (showLoader) {
                setInboxLoading(true);
            }
            setInboxError('');
            const [, allItems] = await Promise.all([
                FetchInboxSummary(),
                FetchInboxTabItems('all'),
                FetchInboxTabItems('notifications'),
                FetchInboxTabItems('messages'),
                FetchInboxTabItems('alerts')
            ]);

            ShowIncomingInboxToasts(Array.isArray(allItems) ? allItems : []);
        } catch (error) {
            console.error('Inbox load error:', error);
            setInboxError('Unable to load communications right now.');
            setInboxCounts(emptyInboxCounts());
            setInboxItems({ all: [], messages: [], alerts: [], notifications: [] });
        } finally {
            if (showLoader) {
                setInboxLoading(false);
            }
        }
    }

    const MarkInboxItemRead = async (inboxId: string) => {
        const response = await fetch(buildApiUrl(baseUrl, `communications/api/inbox/${inboxId}/read/`), {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
                staff_id: staffId,
                institution_id: institutionId
            })
        });

        if (!response.ok) {
            throw new Error('Failed to mark inbox item as read');
        }
    }

    const MarkAllInboxItemsRead = async () => {
        try {
            await fetch(buildApiUrl(baseUrl, `communications/api/inbox/mark-all-read/`), {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    staff_id: staffId,
                    institution_id: institutionId
                })
            });
            await RefreshInboxData();
        } catch (error) {
            console.error('Mark all read error:', error);
        }
    }

    const OpenInboxItem = async (item: CommunicationInboxItem) => {
        try {
            if (!item.is_read) {
                await MarkInboxItemRead(item.inbox_id);
                await RefreshInboxData();
            }
        } catch (error) {
            console.error('Inbox open error:', error);
        }

        if (item.action_url) {
            window.location.href = item.action_url;
        }
    }

    const FormatInboxAge = (timestamp: string) => {
        const diffMs = Date.now() - new Date(timestamp).getTime();
        const diffMinutes = Math.max(1, Math.floor(diffMs / 60000));

        if (diffMinutes < 60) {
            return `${diffMinutes} min ago`;
        }

        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours < 24) {
            return `${diffHours} hr ago`;
        }

        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    }

    const activeInboxItems = inboxItems[activeInboxTab] || [];
    const activeInboxPageUrl = activeInboxTab === 'messages' ? '/institution/messaging' : '/institution/notifications';

    return(
        <div>
            <header id="page-topbar">
                <div className="layout-width">
                    <div className="navbar-header">
                        <div className="d-flex">
                            {/* Logo */}
                            <div className="navbar-brand-box horizontal-logo">
                                <a href={"/institutions/home"} className="logo logo-dark" >
                                <span className="logo-sm">
                                    <img src={`${process.env.PUBLIC_URL}/assets/images/uzima-logo-icon.svg`} alt="Uzima Healthcare" height="22" />
                                </span>
                                    <span className="logo-lg">
                                    <img src={`${process.env.PUBLIC_URL}/assets/images/uzima-logo-sm.svg`} alt="Uzima Healthcare" height="17" />
                                </span>
                                </a>

                                <a href={"/institution/home"} className="logo logo-light">
                                <span className="logo-sm">
                                    <img src={`${process.env.PUBLIC_URL}/assets/images/uzima-logo-icon.svg`} alt="Uzima Healthcare" height="22" />
                                </span>
                                    <span className="logo-lg">
                                    <img src={`${process.env.PUBLIC_URL}/assets/images/uzima-logo-sm.svg`} alt="Uzima Healthcare" height="17" />
                                </span>
                                </a>
                            </div>

                            <button type="button" className="btn btn-sm px-3 fs-16 header-item vertical-menu-btn topnav-hamburger" id="topnav-hamburger-icon"
                                onClick={() => {
                                    const body = document.documentElement;
                                    const sidebar = document.querySelector('.app-menu');
                                    if (window.innerWidth < 992) {
                                        // Mobile: toggle vertical-sidebar-enable class on html element
                                        body.classList.toggle('vertical-sidebar-enable');
                                        // Also toggle a backdrop
                                        let backdrop = document.getElementById('mobile-menu-backdrop');
                                        if (body.classList.contains('vertical-sidebar-enable')) {
                                            if (!backdrop) {
                                                backdrop = document.createElement('div');
                                                backdrop.id = 'mobile-menu-backdrop';
                                                backdrop.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.4);z-index:999;';
                                                backdrop.onclick = () => {
                                                    body.classList.remove('vertical-sidebar-enable');
                                                    backdrop?.remove();
                                                };
                                                document.body.appendChild(backdrop);
                                            }
                                        } else {
                                            backdrop?.remove();
                                        }
                                    } else {
                                        // Desktop: collapse sidebar
                                        body.setAttribute('data-sidebar-size',
                                            body.getAttribute('data-sidebar-size') === 'sm' ? 'lg' : 'sm'
                                        );
                                    }
                                }}>
                            <span className="hamburger-icon">
                                <span> </span>
                                <span> </span>
                                <span> </span>
                            </span>
                            </button>

                            <form className="app-search d-none d-md-block">
                                <div className="position-relative">
                                    <input type="text" className="form-control" placeholder="Search..." autoComplete="off" id="search-options" value="" />
                                        <span className="mdi mdi-magnify search-widget-icon"> </span>
                                        <span className="mdi mdi-close-circle search-widget-icon search-widget-icon-close d-none" id="search-close-options"> </span>
                                </div>
                            </form>
                        </div>

                        <div className="d-flex align-items-center">

                            <div className="dropdown d-md-none topbar-head-dropdown header-item">
                                <button type="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle" id="page-header-search-dropdown" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                    <i className="bx bx-search fs-22">  </i>
                                </button>
                                <div className="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0"
                                     aria-labelledby="page-header-search-dropdown">
                                    <form className="p-3">
                                        <div className="form-group m-0">
                                            <div className="input-group">
                                                <input type="text" className="form-control" placeholder="Search ..." aria-label="Recipient's username" />
                                                    <button className="btn btn-primary" type="submit"><i className="mdi mdi-magnify"> </i> </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>

                            <div className="dropdown ms-1 topbar-head-dropdown header-item">
                                <button type="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                    <img id="header-lang-img" src={ `${process.env.PUBLIC_URL}/assets/images/flags/us.svg`} alt="Header Language" height="20" width="26" className="rounded" style={{ objectFit: 'cover' }} />
                                </button>
                                <div className="dropdown-menu dropdown-menu-end">


                                    <a href="javascript:void(0);" className="dropdown-item notify-item language py-2" data-lang="en" title="English">
                                        <img src={`${process.env.PUBLIC_URL}/assets/images/flags/us.svg`} alt="user-image" className="me-2 rounded" height="18" width="24" style={{ objectFit: 'cover' }} />
                                            <span className="align-middle">English</span>
                                    </a>

                                    <a href="javascript:void(0);" className="dropdown-item notify-item language" data-lang="sp" title="Spanish">
                                        <img src={`${process.env.PUBLIC_URL}/assets/images/flags/spain.svg`} alt="user-image" className="me-2 rounded" height="18" width="24" style={{ objectFit: 'cover' }} />
                                            <span className="align-middle">Española</span>
                                    </a>

                                    <a href="javascript:void(0);" className="dropdown-item notify-item language"
                                       data-lang="gr" title="German">
                                        <img src={`${process.env.PUBLIC_URL}/assets/images/flags/germany.svg`} alt="user-image" className="me-2 rounded" height="18" width="24" style={{ objectFit: 'cover' }} />
                                        <span className="align-middle">Deutsche</span>
                                    </a>

                                    <a href="javascript:void(0);" className="dropdown-item notify-item language"
                                       data-lang="it" title="Italian">
                                        <img src={`${process.env.PUBLIC_URL}/assets/images/flags/italy.svg`} alt="user-image" className="me-2 rounded" height="18" width="24" style={{ objectFit: 'cover' }} />
                                            <span className="align-middle">Italiana</span>
                                    </a>

                                    <a href="javascript:void(0);" className="dropdown-item notify-item language"
                                       data-lang="ch" title="Chinese">
                                        <img src={`${process.env.PUBLIC_URL}/assets/images/flags/china.svg`} alt="user-image" className="me-2 rounded" height="18" width="24" style={{ objectFit: 'cover' }} />
                                            <span className="align-middle">中国人</span>
                                    </a>


                                    <a href="javascript:void(0);" className="dropdown-item notify-item language"
                                       data-lang="fr" title="French">
                                        <img src={`${process.env.PUBLIC_URL}/assets/images/flags/french.svg`} alt="user-image" className="me-2 rounded" height="18" width="24" style={{ objectFit: 'cover' }} />
                                            <span className="align-middle">Français</span>
                                    </a>
                                </div>
                            </div>


                            <div className="ms-1 header-item d-none d-sm-flex">
                                <button type="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle" onClick={toggleFullscreen}>
                                    <i className='bx bx-fullscreen fs-22'> </i>
                                </button>
                            </div>

                            <div className="ms-1 header-item d-none d-sm-flex">
                                <button type="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle light-dark-mode">
                                    <i className='bx bx-moon fs-22'> </i>
                                </button>
                            </div>

                            <div className="dropdown topbar-head-dropdown ms-1 header-item" id="notificationDropdown">
                                <button type="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle" id="page-header-notifications-dropdown" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-haspopup="true" aria-expanded="false">
                                    <i className='bx bx-bell fs-22'> </i>
                                    <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">{inboxCounts.unread_total}
                                        <span className="visually-hidden">unread messages</span></span>
                                </button>
                                <div className="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0"
                                     aria-labelledby="page-header-notifications-dropdown">

                                    <div className="dropdown-head bg-primary bg-pattern rounded-top">
                                        <div className="p-3">
                                            <div className="row align-items-center">
                                                <div className="col">
                                                    <h6 className="m-0 fs-16 fw-semibold text-white"> Notifications </h6>
                                                </div>
                                                <div className="col-auto dropdown-tabs">
                                                    <span className="badge badge-soft-light fs-13"> {inboxCounts.unread_total} New</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="px-2 pt-2">
                                            <ul className="nav nav-tabs dropdown-tabs nav-tabs-custom" id="notificationItemsTab" role="tablist">
                                                <li className="nav-item waves-effect waves-light">
                                                    <button type="button" className={`nav-link ${activeInboxTab === 'all' ? 'active' : ''}`} onClick={() => setActiveInboxTab('all')}>
                                                        All ({inboxCounts.all})
                                                    </button>
                                                </li>
                                                <li className="nav-item waves-effect waves-light">
                                                    <button type="button" className={`nav-link ${activeInboxTab === 'notifications' ? 'active' : ''}`} onClick={() => setActiveInboxTab('notifications')}>
                                                        Notifications ({inboxCounts.notifications})
                                                    </button>
                                                </li>
                                                <li className="nav-item waves-effect waves-light">
                                                    <button type="button" className={`nav-link ${activeInboxTab === 'messages' ? 'active' : ''}`} onClick={() => setActiveInboxTab('messages')}>
                                                        Messages ({inboxCounts.messages})
                                                    </button>
                                                </li>
                                                <li className="nav-item waves-effect waves-light">
                                                    <button type="button" className={`nav-link ${activeInboxTab === 'alerts' ? 'active' : ''}`} onClick={() => setActiveInboxTab('alerts')}>
                                                        Alerts ({inboxCounts.alerts})
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>

                                    </div>

                                    <div className="tab-content position-relative" id="notificationItemsTabContent">
                                        <div className="py-2 ps-2">
                                            <div data-simplebar style={{maxHeight: '300px'}} className="pe-2">
                                                {inboxLoading ? (
                                                    <div className="text-center py-4 text-muted">
                                                        Loading communications...
                                                    </div>
                                                ) : inboxError ? (
                                                    <div className="text-center py-4 text-muted px-3">
                                                        {inboxError}
                                                    </div>
                                                ) : activeInboxItems.length === 0 ? (
                                                    <div className="text-center py-4 text-muted px-3">
                                                        No {activeInboxTab === 'all' ? 'communications' : activeInboxTab} available right now.
                                                    </div>
                                                ) : (
                                                    activeInboxItems.map((item) => (
                                                        <button
                                                            key={item.inbox_id}
                                                            type="button"
                                                            className="text-reset notification-item d-block dropdown-item position-relative w-100 text-start"
                                                            onClick={() => OpenInboxItem(item)}
                                                        >
                                                            <div className="d-flex">
                                                                <div className="avatar-xs me-3">
                                                                    <span className={`avatar-title rounded-circle fs-16 ${item.item_type === 'alert' ? 'bg-soft-danger text-danger' : item.item_type === 'message' ? 'bg-soft-info text-info' : 'bg-soft-success text-success'}`}>
                                                                        <i className={item.item_type === 'alert' ? 'bx bx-error-circle' : item.item_type === 'message' ? 'bx bx-message-square-dots' : 'bx bx-bell'}> </i>
                                                                    </span>
                                                                </div>
                                                                <div className="flex-1">
                                                                    <h6 className="mt-0 mb-1 fs-13 fw-semibold">{item.title}</h6>
                                                                    <div className="fs-13 text-muted">
                                                                        <p className="mb-1">{item.body}</p>
                                                                    </div>
                                                                    <p className="mb-0 fs-11 fw-medium text-uppercase text-muted">
                                                                        <span><i className="mdi mdi-clock-outline"> </i> {FormatInboxAge(item.created_at)}</span>
                                                                        {item.sender_name ? <span className="ms-2">{item.sender_name}</span> : null}
                                                                    </p>
                                                                </div>
                                                                <div className="px-2 fs-15 d-flex flex-column align-items-end justify-content-center gap-2">
                                                                    {!item.is_read ? <span className="badge bg-danger-subtle text-danger">New</span> : <span className="badge bg-light text-muted">Read</span>}
                                                                    <span className={`badge ${item.priority === 'critical' ? 'bg-danger' : item.priority === 'high' ? 'bg-warning text-dark' : item.priority === 'medium' ? 'bg-primary' : 'bg-secondary'}`}>{item.priority}</span>
                                                                </div>
                                                            </div>
                                                        </button>
                                                    ))
                                                )}

                                                <div className="my-3 text-center view-all d-flex flex-column gap-2 px-3">
                                                    <Link to={activeInboxPageUrl} className="btn btn-soft-success waves-effect waves-light">
                                                        View {activeInboxTab === 'messages' ? 'All Messages' : activeInboxTab === 'alerts' ? 'All Alerts' : activeInboxTab === 'notifications' ? 'All Notifications' : 'All Communications'} <i className="ri-arrow-right-line align-middle"> </i>
                                                    </Link>
                                                    {inboxCounts.unread_total > 0 && (
                                                        <button type="button" className="btn btn-link text-decoration-none" onClick={MarkAllInboxItemsRead}>
                                                            Mark all as read
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="dropdown ms-sm-3 header-item topbar-user">
                                <button type="button" className="btn" id="page-header-user-dropdown"
                                        data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                <span className="d-flex align-items-center">
                                    <img className="rounded-circle header-profile-user" src={`${process.env.PUBLIC_URL}/assets/images/users/${avatar}`} alt="Header Avatar" />
                                    <span className="text-start ms-xl-2">
                                        <span className="d-none d-xl-inline-block ms-1 fw-medium user-name-text">{fname} {lname}</span>
                                        <span
                                            className="d-none d-xl-block ms-1 fs-12 text-muted user-name-sub-text">{clinicName}
                                        </span>
                                    </span>
                                </span>
                                </button>
                                <div className="dropdown-menu dropdown-menu-end">

                                    <h6 className="dropdown-header">Hi {fname} {lname}!</h6>
                                    <Link className="dropdown-item" to="/institution/profile">
                                        <i className="mdi mdi-account-circle text-muted fs-16 align-middle me-1"> </i>
                                        <span className="align-middle">Profile</span>
                                    </Link>
                                    <Link className="dropdown-item" to="/institution/telehealth/messaging">
                                        <i className="mdi mdi-message-text-outline text-muted fs-16 align-middle me-1"> </i>
                                        <span className="align-middle">Messages</span>
                                    </Link>
                                    <Link className="dropdown-item" to="/institution/taskboard">
                                        <i className="mdi mdi-calendar-check-outline text-muted fs-16 align-middle me-1"> </i>
                                        <span className="align-middle">Taskboard</span>
                                    </Link>
                                    <Link className="dropdown-item" to="/institution/faqs">
                                        <i className="mdi mdi-lifebuoy text-muted fs-16 align-middle me-1"> </i>
                                        <span className="align-middle">Help</span>
                                    </Link>
                                    <div className="dropdown-divider">

                                    </div>
                                    <Link className="dropdown-item" to={"/institution/billing"}>
                                        <i className="mdi mdi-wallet text-muted fs-16 align-middle me-1"> </i>
                                        <span className="align-middle">Balance : <b>Kes. {wallet}</b></span>
                                    </Link>
                                    <Link className="dropdown-item" to={"/institution/settings"}>
                                        <span className="badge bg-soft-success text-success mt-1 float-end">New</span>
                                        <i className="mdi mdi-cog-outline text-muted fs-16 align-middle me-1"> </i>
                                        <span className="align-middle">Settings</span>
                                    </Link>
                                    <Link className="dropdown-item" to={"/institution/api"}>
                                        <i className="mdi mdi-api text-muted fs-16 align-middle me-1"> </i>
                                        <span className="align-middle">API Management</span>
                                    </Link>
                                    <Link className="dropdown-item" to={"/institution/lock"}><i className="mdi mdi-lock text-muted fs-16 align-middle me-1"> </i>
                                        <span className="align-middle">Lock Account</span>
                                    </Link>
                                    <Link className="dropdown-item" to={"/institution/newlog"} onClick={handleLogout}>
                                        <i className="mdi mdi-logout text-muted fs-16 align-middle me-1"> </i>
                                        <span className="align-middle" data-key="t-logout">Logout</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
            <InstitutionAIAssistant />

        </div>
    )
}

export default DashboardHeader;